/**
 * Section Component Registry
 *
 * Dynamic component loader for experience page sections.
 * Maps component names from database to React components.
 *
 * Pattern: Registry Pattern + Lazy Loading
 * Epic: MVP 1.20 - Dynamic Experience Page Layout
 */

import { lazy } from 'react';

/**
 * Section component registry
 * Maps componentName (from database) to lazy-loaded React components
 */
const sectionRegistry = {
  // ============================================================================
  // Core Sections (7)
  // ============================================================================
  // Note: HeroSection removed - hero data is only for ImageGallery storage/editing,
  // not for dynamic section rendering

  AboutExperience: lazy(() => import('../../components/experiences/sections/AboutExperience')),
  WhatsIncluded: lazy(() => import('../../components/experiences/sections/WhatsIncluded')),
  MeetYourGuide: lazy(() => import('../../components/experiences/sections/MeetYourGuide')),
  KeyLocations: lazy(() => import('../../components/experiences/sections/KeyLocations')),
  // SocialProof removed - now rendered statically below hero image for all experiences
  // BookingCard removed - stub was archived, real BookingCard is imported directly via guide/BookingCard

  // ============================================================================
  // Activity-Specific Sections (9)
  // ============================================================================

  TargetSpecies: lazy(() => import('../../components/experiences/sections/TargetSpecies')),
  LicensingRequirements: lazy(() => import('../../components/experiences/sections/LicensingRequirements')),
  WaterProfile: lazy(() => import('../../components/experiences/sections/WaterProfile')),
  VesselInfo: lazy(() => import('../../components/experiences/sections/VesselInfo')),
  TechnicalRating: lazy(() => import('../../components/experiences/sections/TechnicalRating')),
  GearList: lazy(() => import('../../components/experiences/sections/GearList')),
  Itinerary: lazy(() => import('../../components/experiences/sections/Itinerary')),
  Curriculum: lazy(() => import('../../components/experiences/sections/Curriculum')),
  RiskDisclosure: lazy(() => import('../../components/experiences/sections/RiskDisclosure')),

  // ============================================================================
  // MVP 1.20.5-S: Fishing & Angling (2)
  // ============================================================================

  TechniquesMethods: lazy(() => import('../../components/experiences/sections/fishing/TechniquesMethods')),
  CatchPolicy: lazy(() => import('../../components/experiences/sections/fishing/CatchPolicy')),

  // ============================================================================
  // MVP 1.20.5-S: Hunting & Game (4)
  // ============================================================================

  SuccessRates: lazy(() => import('../../components/experiences/sections/hunting/SuccessRates')),
  HuntingTags: lazy(() => import('../../components/experiences/sections/hunting/HuntingTags')),
  GameProcessing: lazy(() => import('../../components/experiences/sections/hunting/GameProcessing')),
  HuntingMethods: lazy(() => import('../../components/experiences/sections/hunting/HuntingMethods')),

  // ============================================================================
  // MVP 1.20.5-S: Water Adventures (4)
  // ============================================================================

  RapidsHighlights: lazy(() => import('../../components/experiences/sections/water/RapidsHighlights')),
  WaterPrerequisites: lazy(() => import('../../components/experiences/sections/water/WaterPrerequisites')),
  WhatToExpect: lazy(() => import('../../components/experiences/sections/water/WhatToExpect')),

  // ============================================================================
  // MVP 1.20.5-S: Mountain & Climbing (3)
  // ============================================================================

  RouteInfo: lazy(() => import('../../components/experiences/sections/climbing/RouteInfo')),
  ObjectiveHazards: lazy(() => import('../../components/experiences/sections/climbing/ObjectiveHazards')),
  Acclimatization: lazy(() => import('../../components/experiences/sections/climbing/Acclimatization')),

  // ============================================================================
  // MVP 1.20.5-S: Hiking & Backpacking (1)
  // ============================================================================

  TrailConditions: lazy(() => import('../../components/experiences/sections/hiking/TrailConditions')),

  // ============================================================================
  // MVP 1.20.5-S: Cycling & Biking (3)
  // ============================================================================

  RouteProfile: lazy(() => import('../../components/experiences/sections/cycling/RouteProfile')),
  SurfaceType: lazy(() => import('../../components/experiences/sections/cycling/SurfaceType')),
  BikeRequirements: lazy(() => import('../../components/experiences/sections/cycling/BikeRequirements')),

  // ============================================================================
  // MVP 1.20.5-S: Winter & Snow (2)
  // ============================================================================

  SnowConditions: lazy(() => import('../../components/experiences/sections/winter/SnowConditions')),
  AvalancheInfo: lazy(() => import('../../components/experiences/sections/winter/AvalancheInfo')),

  // ============================================================================
  // MVP 1.20.5-S: Wildlife & Nature (3)
  // ============================================================================

  SpeciesViewing: lazy(() => import('../../components/experiences/sections/wildlife/SpeciesViewing')),
  ViewingConditions: lazy(() => import('../../components/experiences/sections/wildlife/ViewingConditions')),
  PhotographyInfo: lazy(() => import('../../components/experiences/sections/wildlife/PhotographyInfo')),

  // ============================================================================
  // MVP 1.20.5-S: Skills & Education (3)
  // ============================================================================

  LearningObjectives: lazy(() => import('../../components/experiences/sections/education/LearningObjectives')),
  CertificationsEarned: lazy(() => import('../../components/experiences/sections/education/CertificationsEarned')),
  Materials: lazy(() => import('../../components/experiences/sections/education/Materials')),

  // ============================================================================
  // MVP 1.20.5-S: Extreme & Adventure (2)
  // ============================================================================

  SafetySystems: lazy(() => import('../../components/experiences/sections/extreme/SafetySystems')),
  TrainingIncluded: lazy(() => import('../../components/experiences/sections/extreme/TrainingIncluded')),

  // ============================================================================
  // MVP 1.20.5-S: Cultural & Heritage (3)
  // ============================================================================

  HistoricalContext: lazy(() => import('../../components/experiences/sections/cultural/HistoricalContext')),
  SitesVisited: lazy(() => import('../../components/experiences/sections/cultural/SitesVisited')),
  GuideBackground: lazy(() => import('../../components/experiences/sections/cultural/GuideBackground')),

  // ============================================================================
  // MVP 1.20.5-S: Wellness & Therapy (4)
  // ============================================================================

  PracticeType: lazy(() => import('../../components/experiences/sections/wellness/PracticeType')),
  FacilitatorCredentials: lazy(() => import('../../components/experiences/sections/wellness/FacilitatorCredentials')),
  ExpectedOutcomes: lazy(() => import('../../components/experiences/sections/wellness/ExpectedOutcomes')),
  SettingDescription: lazy(() => import('../../components/experiences/sections/wellness/SettingDescription')),

  // ============================================================================
  // MVP 1.20.5-S: Shared Sections (4)
  // ============================================================================

  DayByDayItinerary: lazy(() => import('../../components/experiences/sections/shared/DayByDayItinerary')),
  TrailMap: lazy(() => import('../../components/experiences/sections/shared/TrailMap')),
  ElevationProfile: lazy(() => import('../../components/experiences/sections/shared/ElevationProfile')),
  PhysicalRequirements: lazy(() => import('../../components/experiences/sections/shared/PhysicalRequirements'))
};

/**
 * Get section component by name
 * @param {string} componentName - Component name from database
 * @returns {React.LazyExoticComponent} Lazy-loaded component
 */
export function getSectionComponent(componentName) {
  const Component = sectionRegistry[componentName];

  if (!Component) {
    console.error(`Section component not found: ${componentName}`);
    // Return fallback component
    return lazy(() => import('../../components/experiences/sections/SectionNotFound'));
  }

  return Component;
}

/**
 * Check if section component exists
 * @param {string} componentName - Component name to check
 * @returns {boolean} True if component exists in registry
 */
export function hasSectionComponent(componentName) {
  return componentName in sectionRegistry;
}

/**
 * Get all registered component names
 * @returns {string[]} Array of component names
 */
export function getRegisteredComponents() {
  return Object.keys(sectionRegistry);
}

export default sectionRegistry;
